import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, Gift, Loader2, Infinity as InfinityIcon, MessageCircle, ScanLine, Sparkles } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const FEATURES = [
  { icon: Sparkles, label: "All 5 languages, A1–C2" },
  { icon: MessageCircle, label: "Stories & real-life LIFE practice" },
  { icon: ScanLine, label: "Full lessons, reviews & speaking" },
  { icon: InfinityIcon, label: "AI practice included" },
];

export default function TrialOffer() {
  const nav = useNavigate();
  const { premium, loadProfile } = useAuth();
  const { t } = useLang();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("year");
  const [loading, setLoading] = useState(false);

  useEffect(() => { client.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (premium) { client.post("/profile/trial-offer-seen").finally(() => nav("/home", { replace: true })); }
  }, [premium, nav]);

  const lookupKey = `premium_${interval === "year" ? "yearly" : "monthly"}_eur`;
  const monthly = plans.find((p) => p.lookup_key === "premium_monthly_eur");
  const yearly = plans.find((p) => p.lookup_key === "premium_yearly_eur");
  const price = interval === "year" ? (yearly?.amount ?? 100) : (monthly?.amount ?? 10);
  const savePct = monthly && yearly ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100) : 17;

  const startTrial = async () => {
    setLoading(true);
    try {
      await client.post("/profile/trial-offer-seen").catch(() => {});
      const { data } = await client.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const skip = async () => {
    await client.post("/profile/trial-offer-seen").catch(() => {});
    await loadProfile();
    nav("/home", { replace: true });
  };

  return (
    <MobileShell nav={false}>
      <div className="relative px-5 py-8 min-h-screen flex flex-col">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="relative mb-1">
            <div className="w-[84px] h-[84px] rounded-[26px] mel-gradient flex items-center justify-center shadow-[0_22px_54px_-14px_rgba(37,99,235,0.8)] ring-1 ring-white/40" data-testid="premium-badge">
              <div style={{
                height: 40, width: 40 * (490 / 243),
                WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
                WebkitMaskSize: "contain", maskSize: "contain",
                WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskPosition: "center", maskPosition: "center",
                background: "#ffffff",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.18))",
              }} className="select-none block" />
            </div>
            <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-[0_6px_16px_-4px_rgba(217,119,6,0.8)] ring-[3px] ring-white"
              style={{ background: "linear-gradient(135deg,#fcd34d 0%,#f59e0b 60%,#d97706 100%)" }}>
              <Crown size={16} className="text-white" fill="currentColor" />
            </span>
          </div>
          <div className="mt-4 inline-flex items-center gap-1.5 text-amber-300 font-bold text-sm bg-amber-400/15 px-3 py-1 rounded-full" data-testid="trial-badge">
            <Gift size={15} /> {t("trial_badge")}
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-white mt-4">{t("trial_title")}</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-[300px]">{t("trial_title_sub")}</p>
        </div>

        <ul className="relative z-10 space-y-2.5 mt-7 mb-6">
          {FEATURES.map((f, i) => (
            <li key={i} className="flex items-center gap-3 text-slate-200">
              <span className="w-8 h-8 rounded-lg bg-blue-500/12 text-blue-600 flex items-center justify-center shrink-0"><f.icon size={17} /></span>
              <span className="text-sm font-medium">{f.label}</span>
            </li>
          ))}
        </ul>

        <div className="relative z-10 grid grid-cols-2 gap-3 mb-5" data-testid="trial-plan-toggle">
          <button onClick={() => setInterval("month")} data-testid="trial-month"
            className={`rounded-2xl p-4 text-left border transition-all press ${interval === "month" ? "border-blue-500 glass-strong" : "border-white/10 glass"}`}>
            <span className="block text-slate-300 text-xs font-semibold">{t("monthly")}</span>
            <span className="block text-white font-heading text-xl font-extrabold mt-1">€ {monthly?.amount ?? 10}</span>
            <span className="text-slate-400 text-xs">/ {t("per_month")}</span>
          </button>
          <button onClick={() => setInterval("year")} data-testid="trial-year"
            className={`relative rounded-2xl p-4 text-left border transition-all press ${interval === "year" ? "border-blue-500 glass-strong" : "border-white/10 glass"}`}>
            <span className="absolute -top-2 right-3 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-400 text-emerald-950">-{savePct}%</span>
            <span className="block text-slate-300 text-xs font-semibold">{t("yearly")}</span>
            <span className="block text-white font-heading text-xl font-extrabold mt-1">€ {yearly?.amount ?? 100}</span>
            <span className="text-slate-400 text-xs">/ {t("per_year")}</span>
          </button>
        </div>

        <div className="relative z-10 glass-strong rounded-2xl p-4 mb-5 text-center" data-testid="trial-price-summary">
          <div className="flex items-end justify-center gap-1">
            <span className="font-heading text-3xl font-extrabold text-white">€ {price}</span>
            <span className="text-slate-400 text-sm mb-1">/ {interval === "year" ? t("per_year") : t("per_month")}</span>
          </div>
          <p className="text-slate-500 text-xs mt-1">{t("cancel_anytime")}</p>
        </div>

        <div className="relative z-10 mt-auto">
          <button onClick={startTrial} disabled={loading} data-testid="start-trial-btn"
            className="w-full mel-gradient text-white font-bold py-4 rounded-2xl press shadow-[0_14px_34px_-14px_rgba(37,99,235,0.9)] disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="animate-spin" size={18} /> {t("opening_word")}</> : <><Check size={18} /> {t("start_3_free")}</>}
          </button>
          <p className="text-center text-xs text-slate-500 mt-3">
            € {price}{interval === "year" ? "/" + t("per_year") : "/" + t("per_month")} · <b className="text-slate-400">{t("cancel_anytime")}</b>
          </p>
          <button onClick={skip} data-testid="skip-trial-btn" className="w-full text-slate-400 font-semibold py-3 mt-1 press">
            {t("skip_now")}
          </button>
        </div>
      </div>
    </MobileShell>
  );
}
